import React from 'react';
import {
    Box, Grid, Button, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Typography, CircularProgress,
    alpha, useTheme, useMediaQuery, Paper, Stack, Divider, Chip,
} from '@mui/material';
import { TransactionForm } from '../components/transactions/TransactionForm';
import { TransactionSummary } from '../components/transactions/TransactionSummary';
import { BASE_URL } from '../common/constant';
import axios from 'axios';
import moment from 'moment';
import { getToken, getRole } from '../Service/Service';
import toast from 'react-hot-toast';
import DataGrid from 'components/table/DataGrid';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HistoryIcon from '@mui/icons-material/History';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

export default function Transactions() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [state, setState] = React.useState<any>({});
    const [open, setOpen] = React.useState(false);
    const [transaction, setTransaction] = React.useState({
        loanaccount: 0,
        svaccount: 0,
        l9payment: 0,
        staffsalary: 0,
        drivertips: 0,
        driverfooding: 0,
        extraexpenses: 0,
        remarks: '',
    });
    const [firstDate] = React.useState(new Date(moment().startOf('day').format('YYYY-MM-DD')));
    const [lastDate] = React.useState(new Date(moment().format('YYYY-MM-DD')));
    const [active, setActive] = React.useState(true);
    const [data, setData] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [historyLoaded, setHistoryLoaded] = React.useState(false);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTransaction({ ...transaction, [event.target.name]: event.target.value });
    };

    const totalExpense = () =>
        Object.values(transaction)
            .filter((v) => typeof v === 'number')
            //@ts-ignore
            .reduce((acc, curr) => acc + curr, 0);

    const finalClosing = () => {
        const sellAmount = Number(state.todaySellAmount) || 0;
        const yesterdayBalance = Number(state.yesterdaybalance) || 0;
        const amountPaid = Number(state.todayAmountPaid) || 0;
        const expense = totalExpense();
        //@ts-ignore
        return sellAmount + yesterdayBalance - amountPaid - expense;
    };

    const todayCash = (): number => Number(state.todayAmountPaid) || 0;

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const handleOk = () => {
        setOpen(false);
        setActive(false);
    };

    const fetchTransactionHistory = async () => {
        try {
            setLoading(true);
            const result = await axios.get(`${BASE_URL}transaction/history`, {
                headers: { encryption: false, token: getToken() },
            });
            if (result.data) {
                setData(result.data.data.transaction);
                setHistoryLoaded(true);
            }
        } catch (error) {
            console.error('Error fetching history:', error);
        } finally {
            setLoading(false);
        }
    };

    const getTodayTransaction = async () => {
        try {
            const result = await axios.post(
                `${BASE_URL}transaction/get`,
                { start_date: firstDate, end_date: lastDate },
                { headers: { encryption: false, token: getToken() } }
            );
            if (result.data) {
                toast.success(result.data.message);
                setState(result.data.data.transaction);
            }
        } catch (error) {
            console.error('Error getting transaction:', error);
            toast.error('Failed to fetch transaction');
        }
    };

    const updateTodayTransaction = async () => {
        try {
            const result = await axios.post(
                `${BASE_URL}transaction/update`,
                {
                    ...transaction,
                    todayexpense: totalExpense(),
                    todayClosing: finalClosing(),
                    todayCashPaid: todayCash(),
                },
                { headers: { encryption: false, token: getToken() } }
            );
            if (result.data) toast.success(result.data.message);
        } catch (error: any) {
            console.error('Update Error:', error);
            toast.error(error.response?.data?.errorMessage || 'Update failed');
        }
    };

    React.useEffect(() => {
        getTodayTransaction();
    }, []);

    const columns = [
        { title: 'Name', field: 'name' },
        { title: 'Load Account SlNo', field: 'loanaccount' },
        { title: 'SV Account', field: 'svaccount' },
        { title: 'L9 Payment', field: 'l9payment' },
        { title: 'Staff Salary', field: 'staffsalary' },
        { title: 'Driver Tips', field: 'drivertips' },
        { title: 'Driver Fooding', field: 'driverfooding' },
        { title: 'Extra Expenses', field: 'extraexpenses' },
        { title: 'Total Expense', field: 'todayexpense' },
        { title: 'Today Closing', field: 'todayClosing' },
        { title: 'Cash Paid', field: 'todayCashPaid' },
        { title: 'Remarks', field: 'remarks' },
    ];

    const grandTotalDue = state.grandTotalDue;
    const breakdownItems = [
        { label: 'L9 Payment', value: state.l9payment },
        { label: 'Load Account', value: state.loanaccount },
        { label: 'SV Account', value: state.svaccount },
        { label: 'Staff Salary', value: state.staffsalary },
        { label: 'Driver Tips', value: state.drivertips },
        { label: 'Driver Fooding', value: state.driverfooding },
        { label: 'Extra Expenses', value: state.extraexpenses },
    ];

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
            {/* Page Header */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, #4a148c 0%, #7b1fa2 60%, #ab47bc 100%)`,
                    px: { xs: 2, sm: 4 },
                    py: { xs: 2.5, sm: 3.5 },
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: 1,
                    gap: 2,
                    boxShadow: `0 4px 20px ${alpha('#7b1fa2', 0.45)}`,
                }}
            >
                <Box
                    sx={{
                        bgcolor: alpha('#fff', 0.15),
                        borderRadius: 2,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <AccountBalanceIcon sx={{ color: '#fff', fontSize: { xs: 28, sm: 36 } }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        fontWeight={700}
                        sx={{ color: '#fff', lineHeight: 1.2 }}
                    >
                        Transactions
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.8), mt: 0.3 }}>
                        Record today's expenses and track daily closing balance
                    </Typography>
                </Box>
                {state.grandTotalDue !== undefined && (
                    <Chip
                        label={`Due: ₹${grandTotalDue ?? 0}`}
                        sx={{
                            bgcolor: alpha('#fff', 0.2),
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            display: { xs: 'none', sm: 'flex' },
                        }}
                    />
                )}
            </Box>

            <Box sx={{ px: { xs: 1, sm: 3 }, pt: 2, pb: 4 }}>
                {/* Main 3-column layout */}
                <Grid container spacing={2}>

                    {/* ── Col 1: Expense Form ── */}
                    <Grid item xs={12} md={4}>
                        <Paper
                            elevation={0}
                            sx={{
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2.5,
                                p: { xs: 2, sm: 2.5 },
                                height: '100%',
                                boxShadow: `0 2px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
                            }}
                        >
                            <TransactionForm
                                transaction={transaction}
                                handleChange={handleChange}
                                totalExpense={totalExpense}
                            />
                        </Paper>
                    </Grid>

                    {/* ── Col 2: Summary + Actions ── */}
                    <Grid item xs={12} md={4}>
                        <Paper
                            elevation={0}
                            sx={{
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2.5,
                                p: { xs: 2, sm: 2.5 },
                                height: '100%',
                                boxShadow: `0 2px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
                            }}
                        >
                            <TransactionSummary
                                state={state}
                                //@ts-ignore
                                totalExpense={totalExpense}
                                finalClosing={finalClosing}
                            />

                            <Divider sx={{ my: 2.5 }} />

                            <Stack spacing={1.5}>
                                {(getRole() === 'admin' || getRole() === 'superadmin') && (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<CheckCircleOutlineIcon />}
                                        onClick={handleClickOpen}
                                        fullWidth
                                        sx={{
                                            borderRadius: 2,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            borderWidth: 2,
                                            '&:hover': { borderWidth: 2 },
                                        }}
                                    >
                                        Confirm Refill Sale Update
                                    </Button>
                                )}
                                <Button
                                    variant="contained"
                                    startIcon={<SaveOutlinedIcon />}
                                    onClick={updateTodayTransaction}
                                    disabled={active}
                                    fullWidth
                                    sx={{
                                        borderRadius: 2,
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        bgcolor: '#7b1fa2',
                                        boxShadow: `0 4px 14px ${alpha('#7b1fa2', 0.4)}`,
                                        '&:hover': {
                                            bgcolor: '#6a1b9a',
                                            boxShadow: `0 6px 18px ${alpha('#7b1fa2', 0.5)}`,
                                        },
                                        '&.Mui-disabled': {
                                            bgcolor: alpha('#7b1fa2', 0.35),
                                            color: alpha('#fff', 0.6),
                                        },
                                    }}
                                >
                                    Save & Update
                                </Button>
                                {active && (
                                    <Typography variant="caption" color="text.disabled" align="center">
                                        Confirm refill sale update first to enable saving
                                    </Typography>
                                )}
                            </Stack>
                        </Paper>
                    </Grid>

                    {/* ── Col 3: Grand Total + Breakdown ── */}
                    <Grid item xs={12} md={4}>
                        <Paper
                            elevation={0}
                            sx={{
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2.5,
                                overflow: 'hidden',
                                height: '100%',
                                boxShadow: `0 2px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
                            }}
                        >
                            {/* Grand Total header */}
                            <Box
                                sx={{
                                    px: 2.5,
                                    py: 2,
                                    background: `linear-gradient(135deg, #4a148c, #7b1fa2)`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography variant="subtitle2" fontWeight={700} sx={{ color: alpha('#fff', 0.85) }}>
                                    Grand Total Due
                                </Typography>
                                <Typography variant="h5" fontWeight={800} sx={{ color: '#fff' }}>
                                    ₹{grandTotalDue ?? 0}
                                </Typography>
                            </Box>

                            {/* Breakdown list */}
                            <Box sx={{ p: 2 }}>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                    sx={{ mb: 1.5, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.65rem' }}
                                >
                                    Expense Breakdown
                                </Typography>

                                <Stack spacing={0.5}>
                                    {breakdownItems.map((item) => (
                                        <Box
                                            key={item.label}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                px: 1.5,
                                                py: 0.75,
                                                borderRadius: 1,
                                                '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.05) },
                                            }}
                                        >
                                            <Typography variant="body2" color="text.secondary">
                                                {item.label}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600} color="text.primary">
                                                ₹{item.value ?? 0}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* ── History Section ── */}
                <Box sx={{ mt: 3 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2.5,
                            overflow: 'hidden',
                            boxShadow: `0 2px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
                        }}
                    >
                        {/* History header */}
                        <Box
                            sx={{
                                px: { xs: 2, sm: 3 },
                                py: 1.5,
                                bgcolor: alpha('#7b1fa2', 0.04),
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                <HistoryIcon sx={{ fontSize: 18, color: '#7b1fa2' }} />
                                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#7b1fa2' }}>
                                    Transaction History
                                </Typography>
                                {historyLoaded && (
                                    <Chip
                                        label={`${data.length} records`}
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontSize: '0.65rem',
                                            fontWeight: 600,
                                            bgcolor: alpha('#7b1fa2', 0.1),
                                            color: '#7b1fa2',
                                        }}
                                    />
                                )}
                            </Stack>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={
                                    loading
                                        ? <CircularProgress size={13} color="inherit" />
                                        : <HistoryIcon sx={{ fontSize: 16 }} />
                                }
                                onClick={fetchTransactionHistory}
                                disabled={loading}
                                sx={{
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    bgcolor: '#7b1fa2',
                                    boxShadow: `0 3px 10px ${alpha('#7b1fa2', 0.35)}`,
                                    '&:hover': { bgcolor: '#6a1b9a' },
                                }}
                            >
                                {loading ? 'Loading...' : historyLoaded ? 'Refresh' : 'Load History'}
                            </Button>
                        </Box>

                        {/* History content */}
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6, gap: 2 }}>
                                <CircularProgress size={24} sx={{ color: '#7b1fa2' }} />
                                <Typography variant="body2" color="text.secondary">
                                    Loading transaction history...
                                </Typography>
                            </Box>
                        ) : data.length > 0 ? (
                            <Box sx={{ '& .MuiPaper-root': { boxShadow: 'none !important' } }}>
                                <DataGrid rows={data} columns={columns} />
                            </Box>
                        ) : (
                            <Box sx={{ py: 6, textAlign: 'center' }}>
                                <HistoryIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
                                <Typography variant="body2" color="text.disabled">
                                    Click "Load History" to view past transactions
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Box>
            </Box>

            {/* Confirm Dialog */}
            <Dialog
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: `0 8px 32px ${alpha('#000', 0.12)}`,
                    },
                }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
                    <Box
                        sx={{
                            bgcolor: alpha(theme.palette.warning.main, 0.1),
                            borderRadius: 1.5,
                            p: 0.75,
                            display: 'flex',
                        }}
                    >
                        <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                    </Box>
                    <Typography fontWeight={700}>Confirm Refill Sale Update</Typography>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 2 }}>
                    <DialogContentText>
                        Have you updated today's refill sale? This will enable the Save & Update button.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={handleClose}
                        variant="outlined"
                        sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                    >
                        No, Cancel
                    </Button>
                    <Button
                        onClick={handleOk}
                        variant="contained"
                        autoFocus
                        sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            textTransform: 'none',
                            bgcolor: '#7b1fa2',
                            boxShadow: `0 4px 12px ${alpha('#7b1fa2', 0.35)}`,
                            '&:hover': { bgcolor: '#6a1b9a' },
                        }}
                    >
                        Yes, Confirmed
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
